import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { UTM_KEYS } from '../lib/signupAttribution';
import { navegadorEmbebido, type NavegadorEmbebido } from '../lib/navegadorEmbebido';
import {
  avisoConChrome,
  conContinuarConGoogle,
  intentParaChrome,
  leerContinuarConGoogle,
  urlConAtribucion,
  type AtribucionGuardada,
} from '../lib/googleEnNavegadorEmbebido';
import { useAperturaAnimada } from '../hooks/useAperturaAnimada';
import { idsDeEstaVisita, trackEvent } from '../lib/posthog';
import { conIdsDeVisita, type IdsDeVisita } from '../lib/posthogEntreNavegadores';

// Google dentro de Instagram y compañía, en Android: antes de ir a Google, un modal ofrece
// abrir Atlas en el navegador y seguir ahí con Google, o registrarse con el correo. Está en
// los CUATRO botones de Google de la app: el registro (`AuthForm`), la auth vieja
// (`AuthCompat`), el alta por link que cobra (`EnterpriseCheckout`) y Tiendanube. Un test
// verifica que ningún botón de Google quede afuera.
//
// Por qué frena el botón: dentro de Instagram Google deja entrar al 56%, y pasando por el
// navegador llega a Chrome el 77% y ahí Google anda el 91% (≈70%). Y avisar DESPUÉS no se
// puede: el que Google rechaza queda en la pantalla de error de Google y no vuelve.
// En iPhone y dentro de Facebook no hay modal: la salida al navegador no funciona ahí.
// Ver ADR-2026-11-02-google-en-instagram-pasa-por-el-navegador (supersede
// ADR-2026-10-20-google-avisa-en-instagram-sin-frenar) y
// ADR-2026-11-02-aviso-de-google-solo-donde-chrome-abre.

/** Si a los 2,5 s la página sigue a la vista, Chrome no se abrió: se intenta Google acá. */
const ESPERA_DEL_SALTO_MS = 2500;
/** Lo más que el toque de "Continuar" espera a PostHog: el salto no espera a la analítica. */
const ESPERA_DE_IDS_AL_TOCAR_MS = 300;
/** Al montar no hay apuro: la barra puede recibir los ids cuando PostHog termine de cargar. */
const ESPERA_DE_IDS_AL_MONTAR_MS = 10_000;

/** Lo que la llegada dejó guardado (`captureAttributionParams`), para que viaje en el link. */
function leerAtribucionGuardada(): AtribucionGuardada {
  const guardado: AtribucionGuardada = {};
  try {
    for (const key of UTM_KEYS) guardado[key] = localStorage.getItem(`atlas:${key}`);
    guardado.ref = localStorage.getItem('atlas:ref');
    guardado.fbc = localStorage.getItem('atlas:fbc');
  } catch {
    // Sin storage no hay nada que sumar: el link va con lo que ya tenga la URL.
  }
  return guardado;
}

/**
 * La URL de esta pantalla con la atribución (y, si ya los hay, los ids de PostHog) pegados,
 * y la deja puesta en la barra.
 *
 * El "⋯ → Abrir en el navegador" de Instagram abre la URL QUE ESTÁ EN LA BARRA, y Chrome o
 * Safari arrancan con el storage vacío: si la atribución no está ahí, el navegador nuevo no
 * sabe de qué anuncio vino. Se usa `replaceState` y no `navigate` a propósito: no re-renderiza
 * la pantalla ni dispara sus efectos (el alta por link lee su query en varios lugares). Se
 * conserva el `state` del historial, que es de React Router.
 */
function urlParaAbrirAfuera(ids: IdsDeVisita | null = null): string {
  // Los ids de PostHog hacen que Chrome siga siendo la misma persona, y no una nueva
  // (ADR-2026-11-03-posthog-sigue-a-la-persona-en-el-navegador).
  const conAtribucion = urlConAtribucion(window.location.href, leerAtribucionGuardada());
  const url = ids ? conIdsDeVisita(conAtribucion, ids) : conAtribucion;
  if (url !== window.location.href) {
    try {
      window.history.replaceState(window.history.state, '', url);
    } catch {
      // Si no se puede reescribir, el botón de Chrome igual usa la URL armada.
    }
  }
  return url;
}

/**
 * Solo en local (`npm run dev`): `?simular=instagram` hace de cuenta que la página está dentro
 * de Instagram en Android, para ver el modal en la compu. En producción no existe.
 */
function userAgentDePrueba(): string | null {
  if (!import.meta.env.DEV) return null;
  try {
    if (new URLSearchParams(window.location.search).get('simular') !== 'instagram') return null;
  } catch {
    return null;
  }
  return 'Mozilla/5.0 (Linux; Android 14; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/129.0 Mobile Safari/537.36 Instagram 352.0.0.40.97 Android';
}

/** El primer campo de correo de la pantalla: las cuatro tienen uno debajo de Google. */
function irAlCorreo() {
  const campo = document.querySelector<HTMLInputElement>('input[type="email"]');
  if (!campo) return;
  campo.scrollIntoView({ block: 'center' });
  campo.focus();
}

/**
 * El hook de cada pantalla con botón de Google. El botón llama a `alTocarGoogle` (nunca
 * directo a su handler) y la pantalla dibuja `avisoGoogle`.
 * - Donde va el modal (`avisoConChrome`: Android fuera de Facebook y Messenger), tocar Google
 *   lo abre. En el resto, va directo a Google como siempre.
 * - En cualquier navegador embebido, al montar, deja la atribución en la URL: el "Abrir en el
 *   navegador" del menú de la app la sigue necesitando.
 * - En el navegador, si la URL trae la marca de `conContinuarConGoogle`, sigue sola a Google.
 */
export function useAvisoGoogleEnApp(
  pantalla: string,
  accion: 'registro' | 'entrada',
  continuarConGoogle: () => void,
  opciones: {
    /** Para la pantalla que tiene el formulario de correo plegado (Tiendanube): lo despliega. */
    abrirCorreo?: () => void;
  } = {},
): { alTocarGoogle: () => void; avisoGoogle: ReactNode } {
  const [embebido] = useState<NavegadorEmbebido | null>(() =>
    typeof navigator === 'undefined' ? null : navegadorEmbebido(userAgentDePrueba() ?? navigator.userAgent),
  );
  const conModal = avisoConChrome(embebido);
  const [abierto, setAbierto] = useState(false);
  const [abriendo, setAbriendo] = useState(false);
  // El handler de la pantalla cambia en cada render; el efecto de montaje y el timer usan el último.
  const continuarRef = useRef(continuarConGoogle);
  continuarRef.current = continuarConGoogle;

  useEffect(() => {
    if (embebido) {
      urlParaAbrirAfuera();
      // Para el "⋯ → Abrir en el navegador" del menú de la app, que abre lo que está en la
      // barra: es la única salida en iPhone y en Facebook.
      let vigente = true;
      void idsDeEstaVisita(ESPERA_DE_IDS_AL_MONTAR_MS).then((ids) => {
        if (vigente && ids) urlParaAbrirAfuera(ids);
      });
      return () => {
        vigente = false;
      };
    }
    // Llegó desde el modal: la marca se saca ANTES de ir a Google, así la vuelta o un "atrás"
    // no lo disparan otra vez (y el doble efecto de StrictMode ya no la encuentra).
    const { continuar, sinMarca } = leerContinuarConGoogle(window.location.href);
    if (!continuar) return;
    try {
      window.history.replaceState(window.history.state, '', sinMarca);
    } catch {
      return;
    }
    trackEvent('google_aviso_en_app_continuado', { pantalla });
    continuarRef.current();
  }, [embebido, pantalla]);

  // Si Chrome se abrió, esta pestaña pasa a segundo plano: al volver a la app no queda el
  // modal abierto. Si a los 2,5 s sigue a la vista, el salto falló y se prueba Google acá.
  useEffect(() => {
    if (!abriendo) return;
    const alOcultarse = () => {
      if (document.visibilityState === 'hidden') {
        setAbriendo(false);
        setAbierto(false);
      }
    };
    document.addEventListener('visibilitychange', alOcultarse);
    const t = window.setTimeout(() => {
      setAbriendo(false);
      setAbierto(false);
      if (document.visibilityState !== 'visible') return;
      trackEvent('google_aviso_en_app_salto_fallido', { app: embebido?.app ?? 'webview', pantalla });
      continuarRef.current();
    }, ESPERA_DEL_SALTO_MS);
    return () => {
      document.removeEventListener('visibilitychange', alOcultarse);
      window.clearTimeout(t);
    };
  }, [abriendo, embebido, pantalla]);

  const app = embebido?.app ?? 'webview';
  const alTocarGoogle = () => {
    if (!conModal) {
      continuarConGoogle();
      return;
    }
    trackEvent('google_aviso_en_app_visto', { app, android: true, pantalla });
    setAbierto(true);
  };
  const alContinuar = () => {
    trackEvent('google_aviso_en_app_eleccion', { opcion: 'abrir_chrome', app, pantalla });
    setAbriendo(true);
    // Ids frescos (la sesión pudo rotar desde que se montó la pantalla), con tope corto.
    void idsDeEstaVisita(ESPERA_DE_IDS_AL_TOCAR_MS).then((ids) => {
      const intent = intentParaChrome(conContinuarConGoogle(urlParaAbrirAfuera(ids)));
      if (!intent) {
        setAbriendo(false);
        setAbierto(false);
        continuarRef.current();
        return;
      }
      window.location.href = intent;
    });
  };
  const alElegirCorreo = () => {
    trackEvent('google_aviso_en_app_eleccion', { opcion: 'correo', app, pantalla });
    setAbierto(false);
    // `flushSync`: el formulario plegado tiene que estar en el DOM antes de buscar el campo,
    // y el foco tiene que darse dentro del toque para que Android abra el teclado.
    if (opciones.abrirCorreo) flushSync(opciones.abrirCorreo);
    irAlCorreo();
  };
  const alCerrar = () => {
    if (abriendo) return;
    trackEvent('google_aviso_en_app_eleccion', { opcion: 'cerro', app, pantalla });
    setAbierto(false);
  };

  return {
    alTocarGoogle,
    avisoGoogle: conModal ? (
      <ModalGoogleEnApp
        abierto={abierto}
        accion={accion}
        abriendo={abriendo}
        onContinuar={alContinuar}
        onCorreo={alElegirCorreo}
        onCerrar={alCerrar}
      />
    ) : null,
  };
}

function ModalGoogleEnApp({
  abierto,
  accion,
  abriendo,
  onContinuar,
  onCorreo,
  onCerrar,
}: {
  abierto: boolean;
  accion: 'registro' | 'entrada';
  abriendo: boolean;
  onContinuar: () => void;
  onCorreo: () => void;
  onCerrar: () => void;
}) {
  const { montado, visible } = useAperturaAnimada(abierto, 150);
  const panelRef = useRef<HTMLDivElement>(null);
  const continuarRef = useRef<HTMLButtonElement>(null);
  const cerrarRef = useRef(onCerrar);
  cerrarRef.current = onCerrar;

  // Foco de diálogo: entra en "Continuar", Tab no se escapa al formulario de atrás, y al
  // cerrar vuelve a donde estaba (el botón de Google), salvo que la persona ya esté en
  // otro lado: "Mejor … con correo" lo deja en el campo de correo.
  // Con `montado` y no sólo `abierto`: en el render en que `abierto` pasa a true el panel
  // todavía no está en el DOM (`useAperturaAnimada` lo monta en el siguiente) y los refs
  // valen null (ADR-2026-11-03-revision-greptile-registro-instagram-2).
  const enPantalla = abierto && montado;
  useEffect(() => {
    if (!enPantalla) return;
    const previo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    continuarRef.current?.focus({ preventScroll: true });
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cerrarRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const botones = Array.from(panelRef.current.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
      if (botones.length === 0) return;
      const primero = botones[0];
      const ultimo = botones[botones.length - 1];
      const dentro = panelRef.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === primero || !dentro)) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && (document.activeElement === ultimo || !dentro)) {
        e.preventDefault();
        primero.focus();
      }
    };
    window.addEventListener('keydown', alTeclear);
    return () => {
      window.removeEventListener('keydown', alTeclear);
      const activo = document.activeElement;
      const sinFocoPropio = !activo || activo === document.body || panelRef.current?.contains(activo);
      if (sinFocoPropio && previo?.isConnected) previo.focus({ preventScroll: true });
    };
  }, [enPantalla]);

  if (!montado) return null;

  // Entrando: sube desde abajo; saliendo: se va hacia arriba, más corto.
  const panel = visible
    ? 'duration-300 opacity-100 translate-y-0 blur-0'
    : abierto
      ? 'duration-300 opacity-0 motion-safe:translate-y-3 motion-safe:blur-[4px]'
      : 'duration-150 opacity-0 motion-safe:-translate-y-3 motion-safe:blur-[4px]';

  return createPortal(
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4 transition-opacity ease-out ${
        visible ? 'opacity-100 duration-300' : 'opacity-0 duration-150'
      }`}
      onClick={onCerrar}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="google-en-app-titulo"
        className={`flex w-full max-w-[360px] flex-col gap-5 rounded-lg bg-white p-6 transition-[opacity,transform,filter] ease-out ${panel}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="google-en-app-titulo" className="text-[18px] font-[700] leading-[24px] text-brand-black">
          {accion === 'registro'
            ? 'Se abrirá Atlas en el navegador para completar el registro con Google'
            : 'Se abrirá Atlas en el navegador para iniciar sesión con Google'}
        </h2>
        <div className="flex flex-col gap-3">
          <button
            ref={continuarRef}
            type="button"
            onClick={onContinuar}
            disabled={abriendo}
            className="w-full rounded-md bg-brand-primary px-6 py-3 text-[16px] font-[700] leading-[22px] text-white transition-[transform,opacity] duration-150 ease-out motion-safe:active:scale-[0.96] disabled:opacity-70 disabled:active:scale-100"
          >
            {abriendo ? 'Abriendo el navegador…' : 'Continuar'}
          </button>
          <button
            type="button"
            onClick={onCorreo}
            disabled={abriendo}
            className="w-full rounded-md border border-neutral-200 bg-white px-6 py-3 text-[16px] font-[700] leading-[22px] text-brand-black transition-[transform,opacity] duration-150 ease-out motion-safe:active:scale-[0.96] disabled:opacity-70 disabled:active:scale-100"
          >
            {accion === 'registro' ? 'Mejor registrarme con correo' : 'Mejor entrar con correo'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
